/**
 * Request validation for the billing module.
 *
 * Every schema is `.strict()`: an unexpected key is a rejected request, not a
 * silently ignored one. In a module whose output becomes an invoice, a typo'd
 * field name that gets dropped is the kind of bug nobody finds until a customer
 * is charged for the wrong thing.
 */
import { z } from "zod";

/** The six billing sources. Mirrors BillingSource in @yazamco/shared-types. */
export const billingSourceSchema = z.enum([
  "365",
  "CSP",
  "ESET",
  "Acronis",
  "SentinelOne",
  "Pulseway",
]);

export const skuImportClassificationSchema = z.enum([
  "new",
  "changed",
  "unchanged",
  "deactivated",
  "missing-from-upload",
  "invalid",
  "duplicate",
  "conflicting",
]);

/** Paging shared by the list endpoints. Bounded so a query cannot ask for everything. */
const pagingSchema = {
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
};

/**
 * 'YYYY-MM'. Mirrors the CHECK constraint on every billing_month column.
 *
 * Declared here rather than beside the snapshot schemas below because the two
 * catalogue list queries use it, and a `const` referenced above its own
 * declaration would be a load-time ReferenceError, not a type error.
 */
export const billingMonthSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "billingMonth must be 'YYYY-MM'");

export const skuListQuerySchema = z
  .object({
    search: z.string().trim().min(1).max(200).optional(),
    productFamily: z.string().trim().min(1).max(100).optional(),
    manufacturer: z.string().trim().min(1).max(200).optional(),
    // Query strings carry "true"/"false"; an absent value means "either".
    active: z
      .enum(["true", "false"])
      .transform((v) => v === "true")
      .optional(),
    mapping: z.enum(["mapped", "unmapped"]).optional(),
    /**
     * Narrow the catalogue to the SKUs one month actually bills: a SKU is "in
     * use" in a month when something that month's records carry is mapped to it.
     * Absent means the whole catalogue, which is what the mapping picker needs —
     * a SKU has to be findable before anything is mapped to it.
     */
    billingMonth: billingMonthSchema.optional(),
    ...pagingSchema,
  })
  .strict();

export const licenseNameListQuerySchema = z
  .object({
    search: z.string().trim().min(1).max(200).optional(),
    source: billingSourceSchema.optional(),
    mapping: z.enum(["mapped", "unmapped", "ambiguous"]).optional(),
    /**
     * Restrict the worklist to licence names the given month's records actually
     * carry. Absent means every name ever seen — the old, and still available,
     * behaviour.
     */
    billingMonth: billingMonthSchema.optional(),
    ...pagingSchema,
  })
  .strict();

export const importRowQuerySchema = z
  .object({
    classification: skuImportClassificationSchema.optional(),
    search: z.string().trim().min(1).max(200).optional(),
    ...pagingSchema,
  })
  .strict();

/**
 * The counts the client displayed when the operator pressed Approve.
 *
 * Optional, but when present they are compared to the batch's stored counts and a
 * mismatch refuses the apply: it proves the preview being approved is the diff
 * about to run. See SkuImportService.approve.
 */
export const approveImportSchema = z
  .object({
    expectedCounts: z
      .object({
        new: z.number().int().min(0),
        changed: z.number().int().min(0),
        unchanged: z.number().int().min(0),
        deactivated: z.number().int().min(0),
        missingFromUpload: z.number().int().min(0),
        invalid: z.number().int().min(0),
        duplicate: z.number().int().min(0),
        conflicting: z.number().int().min(0),
      })
      .strict()
      .optional(),
  })
  .strict();

export const createMappingSchema = z
  .object({
    skuId: z.string().uuid(),
    licenseNameId: z.string().uuid(),
    /**
     * Which billing term the mapping answers for. Omitted means 'any' — the
     * same SKU for both terms — so an existing caller keeps its behaviour, and
     * splitting a licence into a monthly and an annual SKU is an explicit act.
     */
    billingPeriod: z.enum(["any", "Monthly", "Annually"]).default("any"),
    /** True when accepting a suggestion; recorded as provenance 'suggested'. */
    fromSuggestion: z.boolean().default(false),
    /** Only meaningful with fromSuggestion; the score the user was shown. */
    matchConfidence: z.number().min(0).max(1).nullable().optional(),
    note: z.string().trim().max(500).nullable().optional(),
  })
  .strict();

/**
 * The name a person chooses for a licence, replacing the vendor's.
 *
 * An empty string is ACCEPTED and means "clear it" — the same convention the
 * FortiGate device note uses, so one endpoint both sets and unsets. 200 is the
 * database CHECK (0040), and it is the export's limit rather than the screen's:
 * the value is written into a Priority charge row.
 *
 * What is NOT checked here is which characters are allowed. That rule belongs to
 * the export (Windows-1255 representability, no tabs or newlines) and is applied
 * by the service against the export's own encoder, so the two can never drift.
 */
export const licenseDisplayNameSchema = z
  .object({
    displayName: z.string().trim().max(200),
  })
  .strict();

/**
 * A catalogue item typed by a person, rather than imported from a price list.
 *
 * The bounds mirror what the columns and the Priority export can carry, not what
 * a form feels like accepting: `sku` is the מק_ט that is written into every
 * charge row, so it is trimmed, non-empty and length-bounded here — and checked
 * against the export's own encoder in the service, which is the only place that
 * knows what Windows-1255 can represent.
 *
 * `null` is accepted for every optional text field and means CLEAR, the same
 * convention the licence display name uses. `active` is a real boolean because
 * this is a JSON body, not a query string.
 */
const skuFieldSchemas = {
  description: z.string().trim().max(500).nullable(),
  barcode: z.string().trim().max(200).nullable(),
  statusRaw: z.string().trim().max(100).nullable(),
  active: z.boolean(),
  productFamily: z.string().trim().max(100).nullable(),
  manufacturer: z.string().trim().max(200).nullable(),
};

export const createSkuSchema = z
  .object({
    sku: z.string().trim().min(1, "A מק_ט is required.").max(100),
    description: skuFieldSchemas.description.optional(),
    barcode: skuFieldSchemas.barcode.optional(),
    statusRaw: skuFieldSchemas.statusRaw.optional(),
    active: skuFieldSchemas.active.optional(),
    productFamily: skuFieldSchemas.productFamily.optional(),
    manufacturer: skuFieldSchemas.manufacturer.optional(),
  })
  .strict();

/**
 * Every field optional — an edit changes what it names and leaves the rest
 * exactly as it was. The refine is what stops an empty body from being counted
 * as an edit: a PATCH that says nothing must not write an audit row claiming
 * something happened.
 */
export const updateSkuSchema = z
  .object({
    sku: z.string().trim().min(1).max(100).optional(),
    description: skuFieldSchemas.description.optional(),
    barcode: skuFieldSchemas.barcode.optional(),
    statusRaw: skuFieldSchemas.statusRaw.optional(),
    active: skuFieldSchemas.active.optional(),
    productFamily: skuFieldSchemas.productFamily.optional(),
    manufacturer: skuFieldSchemas.manufacturer.optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, {
    message: "Name at least one field to change.",
  });

export type CreateSkuInput = z.infer<typeof createSkuSchema>;
export type UpdateSkuInput = z.infer<typeof updateSkuSchema>;

export type SkuListQueryInput = z.infer<typeof skuListQuerySchema>;
export type LicenseNameListQueryInput = z.infer<typeof licenseNameListQuerySchema>;
export type ImportRowQueryInput = z.infer<typeof importRowQuerySchema>;
export type ApproveImportInput = z.infer<typeof approveImportSchema>;
export type CreateMappingInput = z.infer<typeof createMappingSchema>;
export type LicenseDisplayNameInput = z.infer<typeof licenseDisplayNameSchema>;

// ── Monthly API snapshots (phase 6) ─────────────────────────────────────────

/** The four vendor-API sources migration 0029's `billing_snapshots` accepts. */
export const API_BILLING_SOURCES = [
  "ESET",
  "Acronis",
  "SentinelOne",
  "Pulseway",
] as const;

export const apiBillingSourceSchema = z.enum(API_BILLING_SOURCES);

// `billingMonthSchema` is declared with the catalogue queries above, which need
// it at module-load time.

export const snapshotStatusSchema = z.enum(["running", "closed", "failed", "unsealed"]);

export const startSnapshotSchema = z
  .object({
    source: apiBillingSourceSchema,
    billingMonth: billingMonthSchema,
  })
  .strict();

export const startAllSnapshotsSchema = z
  .object({
    billingMonth: billingMonthSchema,
  })
  .strict();

/**
 * Release a closed snapshot's (source, billingMonth) slot so the month can be
 * collected again (migration 0033).
 *
 * The reason is REQUIRED and is stored on the row, not merely audited: unsealing
 * is the one operation that makes an "immutable" month change, and the next
 * person asking why this month's Pulseway figure differs from the one they
 * exported must be able to read the answer off the snapshot itself. Bounded to
 * the same 500 characters as other stored operator text.
 */
export const unsealSnapshotSchema = z
  .object({
    reason: z.string().trim().min(3, "Say why this month is being collected again.").max(500),
  })
  .strict();

export const snapshotListQuerySchema = z
  .object({
    source: apiBillingSourceSchema.optional(),
    billingMonth: billingMonthSchema.optional(),
    status: snapshotStatusSchema.optional(),
    ...pagingSchema,
  })
  .strict();

/**
 * The finding codes a snapshot's collection may persist. Mirrors
 * `BillingFindingCode` in @yazamco/shared-types — kept as its own zod enum
 * (rather than derived from the type) because a type has no runtime values to
 * validate against.
 */
export const billingFindingCodeSchema = z.enum([
  "CUSTOMER_ID_MISSING",
  "CUSTOMER_NOT_FOUND",
  "MULTIPLE_DEPLOYED_COMPONENTS",
  "NO_DEPLOYED_COMPONENT",
  "UNKNOWN_ORGANIZATION",
  "ADAPTER_WARNING",
]);

export const billingFindingSchema = z
  .object({
    source: apiBillingSourceSchema,
    code: billingFindingCodeSchema,
    severity: z.enum(["info", "warning", "blocking"]),
    blocking: z.boolean(),
    message: z.string().max(2000),
    customerId: z.string().max(200).nullable().optional(),
  })
  .strict();

/**
 * Validates one canonical {@link BillingRecord} (shared-types) from an API
 * adapter before it may be persisted to `billing_records`.
 *
 * Deliberately narrower than the column types: `billing_records.number_of_items`
 * is NUMERIC(20,3) and `quantity_for_calc` is NUMERIC(38,8), but a JS `number`
 * loses precision long before either ceiling, and a value anywhere near them is
 * certainly a broken adapter, not a real quantity — so the bound here is tight
 * enough to catch that while leaving vendor-realistic values untouched.
 *
 * Per decision #4 every API source is billed as a full month: `billingPeriod`,
 * `prorataFraction` and the `quantityForCalc === numberOfItems` identity are
 * enforced here rather than trusted from the adapter, so a future adapter bug
 * fails this validation instead of silently mis-billing.
 */
export const apiBillingRecordSchema = z
  .object({
    customerId: z.string().trim().min(1).max(200).nullable(),
    sourceCustomerName: z.string().max(500).nullable().optional(),
    source: apiBillingSourceSchema,
    subscriptionName: z.string().trim().min(1).max(300),
    metricKey: z.string().max(200).nullable().optional(),
    /** A SKU the SOURCE supplied — Acronis's billing report does. Provenance
     *  only: the SKU an invoice exports against comes from the mapping. */
    sku: z.string().max(200).nullable().optional(),
    startDate: z.date(),
    endDate: z.date(),
    numberOfItems: z.number().finite().nonnegative().max(1_000_000_000),
    quantityKind: z.enum(["count", "gb"]),
    billingPeriod: z.literal("Monthly"),
    prorataFraction: z.literal(1),
    quantityForCalc: z.number().finite().nonnegative().max(1_000_000_000),
    flags: z.record(z.string(), z.unknown()).optional(),
  })
  .strict()
  .superRefine((record, ctx) => {
    if (record.quantityKind === "count" && !Number.isInteger(record.numberOfItems)) {
      ctx.addIssue({
        code: "custom",
        message: "a 'count' record must have a whole numberOfItems",
        path: ["numberOfItems"],
      });
    }
    if (record.quantityKind === "gb") {
      const scaled = Math.round(record.numberOfItems * 1000);
      if (Math.abs(scaled - record.numberOfItems * 1000) > 1e-6) {
        ctx.addIssue({
          code: "custom",
          message: "a 'gb' record may carry at most 3 decimal places",
          path: ["numberOfItems"],
        });
      }
    }
    if (record.quantityForCalc !== record.numberOfItems) {
      ctx.addIssue({
        code: "custom",
        message:
          "quantityForCalc must equal numberOfItems for an API source (prorataFraction is always 1)",
        path: ["quantityForCalc"],
      });
    }
  });

export type StartSnapshotInput = z.infer<typeof startSnapshotSchema>;
export type StartAllSnapshotsInput = z.infer<typeof startAllSnapshotsSchema>;
export type SnapshotListQueryInput = z.infer<typeof snapshotListQuerySchema>;
export type UnsealSnapshotInput = z.infer<typeof unsealSnapshotSchema>;
export type ApiBillingRecordInput = z.infer<typeof apiBillingRecordSchema>;

// ── 365/CSP uploads (phase 7) ────────────────────────────────────────────────

/** The two file sources migration 0029's `billing_upload_batches` accepts. */
export const billingFileSourceSchema = z.enum(["365", "CSP"]);

export const uploadStageQuerySchema = z
  .object({
    source: billingFileSourceSchema,
    billingMonth: billingMonthSchema,
  })
  .strict();

export const uploadRowOutcomeSchema = z.enum(["skipped", "invalid", "recorded"]);

export const uploadRowFilterSchema = z
  .object({
    outcome: uploadRowOutcomeSchema.optional(),
    search: z.string().trim().min(1).max(200).optional(),
    ...pagingSchema,
  })
  .strict();

export const uploadBatchStatusSchema = z.enum(["staged", "parsed", "discarded"]);

export const uploadListQuerySchema = z
  .object({
    source: billingFileSourceSchema.optional(),
    billingMonth: billingMonthSchema.optional(),
    status: uploadBatchStatusSchema.optional(),
    ...pagingSchema,
  })
  .strict();

export type UploadStageQueryInput = z.infer<typeof uploadStageQuerySchema>;
export type UploadRowFilterInput = z.infer<typeof uploadRowFilterSchema>;
export type UploadListQueryInput = z.infer<typeof uploadListQuerySchema>;

// ── Billing run reconciliation (phase 8) ─────────────────────────────────────

export const billingRunStatusSchema = z.enum(["draft", "validated", "exported", "discarded"]);

export const createRunSchema = z
  .object({
    billingMonth: billingMonthSchema,
    snapshotIds: z.array(z.string().uuid()).max(4).default([]),
    uploadBatchIds: z.array(z.string().uuid()).max(2).default([]),
  })
  .strict()
  .refine((v) => v.snapshotIds.length > 0 || v.uploadBatchIds.length > 0, {
    message: "Select at least one snapshot or upload batch.",
  });

export const runListQuerySchema = z
  .object({
    billingMonth: billingMonthSchema.optional(),
    status: billingRunStatusSchema.optional(),
    ...pagingSchema,
  })
  .strict();

export const eligibleRunSourcesQuerySchema = z
  .object({
    billingMonth: billingMonthSchema,
  })
  .strict();

const resolveCustomerTargetSchema = z.union([
  z.object({ vendorCustomerId: z.string().trim().min(1).max(200), source: billingSourceSchema.optional() }).strict(),
  z.object({ recordId: z.string().uuid() }).strict(),
]);

export const resolveCustomerSchema = z
  .object({
    target: resolveCustomerTargetSchema,
    // The LOCAL business customer id, never an internal UUID — see
    // ResolveCustomerRequest in shared-types for why.
    customerId: z.string().trim().min(1).max(200),
    /**
     * Take `customerId` at face value when the directory does not know it.
     *
     * Defaulted rather than optional so the service reads one boolean instead of
     * `?? false` at every branch. What it does NOT do is skip validation: the
     * service still refuses an id the Priority export could not write, because
     * an id typed by hand ends up in a charge row exactly like a picked one.
     */
    manual: z.boolean().default(false),
    note: z.string().trim().max(500).optional(),
  })
  .strict();

const resolveLicenseSkuTargetSchema = z.union([
  z
    .object({
      source: billingSourceSchema,
      subscriptionName: z.string().trim().min(1).max(300),
      // Optional, and omission means "every term" — the pre-split behaviour.
      billingPeriod: z.enum(["Monthly", "Annually"]).optional(),
      /**
       * Optional business customer number. Present, the fan-out stays inside
       * that one customer's records; omitted, it spans the whole run — the
       * pre-existing behaviour, kept so an operator can still fix one licence
       * for everyone in a single act.
       */
      customerId: z.string().trim().min(1).max(200).optional(),
    })
    .strict(),
  z.object({ recordId: z.string().uuid() }).strict(),
]);

export const resolveLicenseSkuSchema = z
  .object({
    target: resolveLicenseSkuTargetSchema,
    skuId: z.string().uuid(),
    note: z.string().trim().max(500).optional(),
  })
  .strict();

export const resolveProrataSpanSchema = z
  .object({
    recordId: z.string().uuid(),
    accepted: z.literal(true),
    note: z.string().trim().max(500).optional(),
  })
  .strict();

/**
 * Which customers an export covers.
 *
 * An empty body is the ordinary case and means "every customer that is ready
 * and has not been exported yet". Naming customers narrows it to exactly those,
 * and `reexport` is the acknowledgement required before an already-exported
 * customer may be written a second time — the server refuses without it rather
 * than deciding on the operator's behalf.
 *
 * The 5000 ceiling is a request-size bound, not a business rule: a month has far
 * fewer customers than that, and an unbounded array is a denial-of-service
 * surface on a route that does real work per element.
 */
export const exportRunSchema = z
  .object({
    customerIds: z.array(z.string().trim().min(1).max(200)).max(5000).optional(),
    reexport: z.boolean().default(false),
  })
  .strict();

export type ExportRunInput = z.infer<typeof exportRunSchema>;

export type CreateRunInput = z.infer<typeof createRunSchema>;
export type RunListQueryInput = z.infer<typeof runListQuerySchema>;
export type EligibleRunSourcesQueryInput = z.infer<typeof eligibleRunSourcesQuerySchema>;
export type ResolveCustomerInput = z.infer<typeof resolveCustomerSchema>;
export type ResolveLicenseSkuInput = z.infer<typeof resolveLicenseSkuSchema>;
export type ResolveProrataSpanInput = z.infer<typeof resolveProrataSpanSchema>;
